import buildReportFilename from './reportFilename';

/**
 * Generate and download a PDF report.
 *
 * Generation runs inside a Web Worker so the main thread stays responsive
 * during the 30-65 second render.  The File System Access save dialog is
 * shown eagerly (before the worker starts) so the user gesture context is
 * preserved.
 *
 * Picking a location in that dialog creates the file on disk immediately, so any path that
 * ends without writing it (cancel, render failure, dead worker) removes it again. Otherwise
 * the user is left with a 0 KB file that looks like a finished export.
 *
 * @param {object}   opts
 * @param {object}   opts.config
 * @param {array}    opts.columns
 * @param {object}   opts.columnWidths
 * @param {array}    opts.data
 * @param {object}   opts.parameters
 * @param {object}   opts.paramHeaderWidths
 * @param {string}   opts.logoSrc
 * @param {string}   [opts.clientLogoSrc]   — base64 data URL for client logo (optional)
 * @param {string}   [opts.clientName]      — client name for confidentiality statement (optional)
 * @param {number}   [opts.rowsPerPage]
 * @param {number}   [opts.fontSizeOverride]
 * @param {function} [opts.isCancelled]      — () => bool
 * @param {function} [opts.onPhase]          — (phase: string, detail?: any) => void
 * @param {function} [opts.onProgress]       — (msg: string|null) => void  (toolbar indicator)
 * @param {string}   [opts.groupByLabel]    — label of the group-by parameter (for filename segment)
 * @param {function} [opts.onWorkerReady]    — (worker, rejectFn) => void  (caller stores ref for cancel)
 */

// A browser that kills the worker (out of memory on a very large report) fires no error
// event, so without a deadline the render Promise never settles: no error, no notification,
// and the activity stays "active" forever. A heartbeat cannot stand in for the deadline:
// @react-pdf lays the document out without yielding, so a healthy worker is as silent as a
// dead one for the whole render. The deadline scales with page count and is generous — the
// slowest healthy render measured was ~1.25 s/page (IP Addresses - Site Level, ~144 pages in
// ~3 min) — so it only ever fires on a render that is not coming back.
const RENDER_DEADLINE_MIN_MS = 5 * 60 * 1000;
const RENDER_DEADLINE_MS_PER_PAGE = 3000;

// Remove the file the save dialog created. remove() is not in every browser; when it is
// missing or refused the file stays, and the error names it so the user knows it is empty.
const discardFile = async (fileHandle) => {
  if (!fileHandle) return true;
  try {
    if (typeof fileHandle.remove === 'function') {
      await fileHandle.remove();
      return true;
    }
  } catch (err) {
    console.warn('[reportPdfExporter] Could not remove the unwritten file:', err);
  }
  return false;
};

const exportReportPdf = async ({
  config,
  columns,
  columnWidths,
  data,
  parameters,
  paramHeaderWidths,
  logoSrc,
  clientLogoSrc,
  clientName,
  sortOrder,
  groupByField,
  groupByLabel,
  rowsPerPage,
  fontSizeOverride,
  qrDataUrl,
  dawnVersion,
  columnsByGroup,
  autoDownload = false,
  isCancelled,
  onPhase,
  onProgress,
  onWorkerReady,
}) => {
  let fileHandle = null;
  let filename = '';
  try {
    if (onProgress) onProgress('Generating PDF...');

    // ── Phase 2: build document structure (instantaneous — just the prop set) ──
    if (onPhase) onPhase('building');
    const pageCount = rowsPerPage
      ? Math.ceil(Math.max(data.length, 1) / rowsPerPage)
      : 1;
    filename = buildReportFilename(config.reportName, 'pdf', groupByLabel);

    // ── Eagerly open the save dialog while we still have the user gesture ──
    // Skip when autoDownload is true (separate-files grouped export — no dialog spam).
    if (!autoDownload && window.showSaveFilePicker) {
      try {
        fileHandle = await window.showSaveFilePicker({
          suggestedName: filename,
          types: [{
            description: 'PDF Document',
            accept: { 'application/pdf': ['.pdf'] },
          }],
        });
      } catch (err) {
        if (err.name === 'AbortError') {
          // User cancelled the save dialog — bail out entirely
          if (onProgress) onProgress(null);
          return false;
        }
        // Unsupported / permission error — fall through to auto-download
      }
    }

    // ── Phase 3: render PDF in Web Worker (non-blocking) ──
    if (onPhase) onPhase('rendering', pageCount);

    const renderStart = Date.now();
    const deadlineMs = Math.max(RENDER_DEADLINE_MIN_MS, pageCount * RENDER_DEADLINE_MS_PER_PAGE);
    let deadline = null;

    const blob = await new Promise((resolve, reject) => {
      const worker = new Worker(
        new URL('../../../workers/pdfExportWorker.js', import.meta.url)
      );

      const settle = (fn, value) => {
        clearTimeout(deadline);
        worker.terminate();
        fn(value);
      };

      // Hand the worker reference + reject fn to the caller so it can
      // terminate the worker immediately on cancel.
      if (onWorkerReady) onWorkerReady(worker, (err) => settle(reject, err));

      deadline = setTimeout(() => {
        console.warn(`[reportPdfExporter] No result after ${Math.round(deadlineMs / 1000)}s on ${pageCount} pages; giving up`);
        settle(reject, new Error(
          `The PDF renderer did not finish ${pageCount} pages in ${Math.round(deadlineMs / 60000)} minutes. The report is probably too large for a PDF; add filters, or export to Excel.`
        ));
      }, deadlineMs);

      worker.onmessage = ({ data: result }) => {
        if (result.type === 'done') {
          settle(resolve, result.blob);
        } else {
          settle(reject, new Error(result.message || 'PDF worker failed'));
        }
      };

      worker.onerror = (err) => {
        console.warn(`[reportPdfExporter] Worker error after ${Math.round((Date.now() - renderStart) / 1000)}s on ${pageCount} pages:`, err.message || err);
        settle(reject, new Error(err.message || 'PDF worker failed'));
      };

      worker.onmessageerror = () => {
        settle(reject, new Error('The PDF renderer returned a result that could not be read.'));
      };

      worker.postMessage({
        config,
        columns,
        columnWidths,
        reportData: data,    // renamed — 'data' is reserved in MessageEvent
        parameters,
        paramHeaderWidths,
        logoSrc,
        clientLogoSrc,
        clientName,
        sortOrder,
        groupByField,
        rowsPerPage,
        fontSizeOverride,
        qrDataUrl,
        dawnVersion,
        columnsByGroup,
      });
    });

    // Seconds per page on real renders is what the deadline and the page ceiling are set from.
    console.info(`[reportPdfExporter] ${pageCount} pages rendered in ${Math.round((Date.now() - renderStart) / 1000)}s (${blob.size} bytes)`);

    // Check if cancelled while worker was running
    if (isCancelled && isCancelled()) {
      await discardFile(fileHandle);
      if (onPhase) onPhase('cancelled');
      if (onProgress) onProgress(null);
      return false;
    }

    // ── Phase 4: write file ──
    if (onPhase) onPhase('download');

    if (fileHandle) {
      // File System Access API — write to the handle the user already picked
      try {
        const writable = await fileHandle.createWritable();
        await writable.write(blob);
        await writable.close();
      } catch (writeErr) {
        console.error('[reportPdfExporter] File write failed, falling back to download:', writeErr);
        // Fall through to blob-URL download
        await discardFile(fileHandle);
        fileHandle = null;
      }
    }

    if (!fileHandle) {
      // Fallback: trigger a browser download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }

    // Report the finished size. Nobody had ever measured a report PDF — the blob goes
    // straight to a download, so the number existed for a moment and was discarded. It decides
    // whether subscribed PDFs can be emailed as attachments (Graph caps a single-request send
    // near 3 MB of file) or need storing and linking. onPhase handlers that take only the phase
    // ignore the second argument, so this is additive.
    if (onPhase) onPhase('ready', { bytes: blob.size });
    if (onProgress) onProgress(null);
    return true;

  } catch (err) {
    const removed = await discardFile(fileHandle);
    // AbortError = worker was terminated by the cancel button
    if (err.name === 'AbortError' || (err.message && err.message.includes('cancel'))) {
      if (onPhase) onPhase('cancelled');
      if (onProgress) onProgress(null);
      return false;
    }
    if (onProgress) onProgress(null);
    if (!removed) {
      // A new Error rather than editing err: a DOMException's message is read-only.
      throw new Error(`${err.message || 'PDF export failed.'} An empty file named "${filename}" was left where you chose to save it; delete it.`);
    }
    throw err;
  }
};

export default exportReportPdf;
